// Help articles, Spanish (Latin America): AI-drafted from articles.en.js on 2026-11-01,
// marked for review (HELP_SYSTEM_PLAN.md, D3). Same ids, categories, screens and links as
// the English; on-screen names match the app's own Spanish labels, and the app's formal
// "usted". Example wording a candidate would say in the interview stays in English.

export const REVIEW_STATUS = "ai_draft";

export const CATEGORIES = [
  { id: "start", title: "Primeros pasos" },
  { id: "setup", title: "Preparar una entrevista" },
  { id: "sound", title: "Micrófono y sonido" },
  { id: "during", title: "Durante la entrevista" },
  { id: "live", title: "Modo Avanzado" },
  { id: "report", title: "Su informe y sus puntuaciones" },
  { id: "account", title: "Cuenta, sesiones y privacidad" },
  { id: "free", title: "Prueba Gratuita" },
];

export const ARTICLES = [
  // ── Primeros pasos ───────────────────────────────────────────────────────
  {
    id: "how-it-works", category: "start", screens: ["upload", "account"],
    title: "Cómo funciona el simulador",
    body: [
      { p: "El English Job Interview Simulator le permite practicar una entrevista de trabajo real en inglés y le muestra exactamente cómo mejorar." },
      { steps: [
        "Suba [su currículum](#upload-resume) y la [descripción del puesto](#job-description) al que aspira.",
        "Elija su modo de práctica: [Modo Guiado o Modo Avanzado](#guided-or-live).",
        "Responda en voz alta las 10 preguntas de la entrevista. El entrevistador puede hacerle una breve [pregunta de seguimiento](#follow-ups).",
        "Lea [su informe](#report-overview): su desempeño en la entrevista, la retroalimentación sobre su inglés profesional y una [versión mejorada de cada respuesta](#polished-answer).",
      ] },
      { p: "Las preguntas se crean a partir de su currículum y de la descripción del puesto, así que cada entrevista es diferente." },
    ],
  },
  {
    id: "app-language", category: "start", screens: ["account"],
    title: "Elegir el idioma de la aplicación",
    body: [
      { p: "El idioma de la aplicación es el idioma de las pantallas, las instrucciones y la retroalimentación. Lo elige al registrarse y puede cambiarlo en Configuración de la cuenta, en el menú de la esquina superior derecha." },
      { p: "La entrevista en sí siempre es en inglés: las preguntas, sus respuestas y las [respuestas mejoradas](#polished-answer). Las explicaciones y los consejos están en el idioma de la aplicación." },
      { p: "La aplicación está disponible en inglés, portugués (Brasil), español (Latinoamérica) y francés (Canadá)." },
    ],
  },
  {
    id: "partner-code", category: "start", screens: ["account"],
    title: "Usar un Código de Socio",
    body: [
      { p: "Un Código de Socio le da acceso, o condiciones especiales de acceso, a través de uno de nuestros socios. Puede ingresarlo al registrarse y crear el perfil de su cuenta, o más tarde en Configuración de la cuenta." },
      { steps: [
        "Abra Configuración de la cuenta.",
        "Escriba el código en el campo Código de Socio, exactamente como lo recibió.",
        "Seleccione Aplicar código. La pantalla muestra la nueva [fecha de fin de su acceso](#access-sessions).",
      ] },
      { note: "Si el código no se acepta, revise que no haya espacios ni letras mal escritas. Si aun así no funciona, comuníquese con el socio que le dio el código." },
    ],
  },
  {
    id: "sign-in", category: "start", screens: ["login"],
    title: "Iniciar sesión y restablecer su contraseña",
    body: [
      { p: "Puede iniciar sesión con su correo electrónico y contraseña, o con su cuenta de Google si se registró con Google." },
      { steps: [
        "Para restablecer su contraseña, seleccione la pestaña Recuperar contraseña en la parte superior del cuadro de inicio de sesión.",
        "Ingrese el correo electrónico con el que se registró.",
        "Abra el correo que le enviamos y siga el enlace para elegir una nueva contraseña.",
      ] },
      { note: "¿No recibió el correo? Revise su carpeta de spam o correo no deseado, y confirme que escribió la misma dirección con la que se registró." },
    ],
  },

  // ── Preparar una entrevista ──────────────────────────────────────────────
  {
    id: "upload-resume", category: "setup", screens: ["upload"],
    title: "Subir su currículum",
    body: [
      { p: "Suba el mismo currículum que le enviaría a un empleador, en PDF o Word (.docx)." },
      { list: [
        "Su currículum debe estar en inglés. Un currículum bilingüe funciona si al menos la mitad está en inglés.",
        "Los nombres de empresas, escuelas y puestos en otros idiomas no son un problema.",
        "Antes que nada, la [revisión de privacidad](#privacy-scan) elimina sus datos personales. Después, usted revisa el resultado.",
      ] },
      { note: "Si su currículum se rechaza por no estar en inglés, suba una versión en inglés." },
    ],
  },
  {
    id: "privacy-scan", category: "setup", screens: ["upload"],
    title: "Qué elimina la revisión de privacidad",
    body: [
      { p: "Antes de usar su currículum, la revisión de privacidad busca datos personales y los reemplaza con etiquetas neutras como [Name] o [Company]." },
      { list: [
        "Datos de contacto: correos electrónicos, números de teléfono, enlaces a sitios web y redes sociales.",
        "Su nombre y los nombres de otras personas.",
        "Direcciones y números de identificación.",
      ] },
      { p: "Sus habilidades, su experiencia y sus puestos se mantienen, porque las preguntas de la entrevista los necesitan." },
      { note: "Lea siempre el texto revisado antes de continuar. La revisión elimina todo lo posible, pero no es perfecta: revise el texto y haga los cambios que necesite. Después, marque la casilla para confirmar que revisó el texto y que aprueba su uso en la entrevista. Vea también [Sus datos y su privacidad](#data-privacy)." },
    ],
  },
  {
    id: "job-description", category: "setup", screens: ["upload"],
    title: "Descripción del puesto real o de ejemplo",
    body: [
      { p: "Para una práctica más realista, pegue o suba la descripción real del puesto al que se está postulando." },
      { p: "Si no se está postulando a un puesto en particular y solo quiere practicar sus habilidades de entrevista, elija una descripción de puesto de ejemplo. Puede elegir un área y un nivel, o dejar que la aplicación cree una descripción de puesto de ejemplo adecuada a su currículum." },
      { p: "Las preguntas de la entrevista combinan su currículum con la descripción del puesto para crear un conjunto de preguntas personalizado para su entrevista. Si el currículum que sube y la descripción del puesto tienen muy poco en común, verá el [aviso de compatibilidad](#fit-check)." },
    ],
  },
  {
    id: "fit-check", category: "setup", screens: ["upload"],
    title: "Qué significa el aviso de compatibilidad",
    body: [
      { p: "Si su currículum y la descripción del puesto tienen muy poco en común, verá un aviso antes de que se cree la entrevista. Las buenas preguntas de práctica necesitan algo de ambos." },
      { note: "El aviso no es una evaluación de si usted es la persona adecuada para el puesto. Solo significa que el simulador puede tener dificultades para crear preguntas relevantes para su entrevista." },
      { p: "Puede:" },
      { list: [
        "Subir otra descripción de puesto.",
        "Dejar que la aplicación le sugiera un puesto de práctica adecuado a su currículum.",
        "Practicar de todos modos.",
      ] },
      { p: "Ver el aviso y cambiar la descripción del puesto no afecta su conteo de sesiones. Practicar de todos modos sí usa una [sesión](#what-uses-session) normal de entrevista." },
    ],
  },
  {
    id: "guided-or-live", category: "setup", screens: ["upload"],
    title: "Modo Guiado o Modo Avanzado: cuál elegir",
    body: [
      { p: "Los dos modos usan las mismas preguntas y la misma evaluación. La diferencia es cómo se vive la entrevista." },
      { list: [
        "Modo Guiado: puede ver cada pregunta, [volver a escucharla](#replay-question), grabar cuando esté listo, escuchar su respuesta y volver a grabarla antes de enviarla. Vea [Grabar y terminar una respuesta](#guided-recording).",
        "Modo Avanzado: como una entrevista real. Solo audio, sin texto. Tiene 30 segundos para empezar a responder, y el entrevistador continúa cuando usted deja de hablar. Vea [Cómo funciona el Modo Avanzado](#live-how).",
      ] },
      { p: "Empiece con el Modo Guiado si es nuevo en las entrevistas en inglés. Elija el Modo Avanzado cuando quiera practicar en condiciones reales." },
      { note: "La [Prueba Gratuita](#free-trial) usa el Modo Guiado. El Modo Avanzado viene con el [acceso completo de 30 días](#access-sessions)." },
    ],
  },
  {
    id: "choose-voice", category: "setup", screens: ["upload"],
    title: "Elegir la voz del entrevistador",
    body: [
      { p: "Elija un entrevistador con voz femenina o masculina en Configuración de la sesión. Seleccione Escuchar esta voz para oír primero una breve muestra." },
      { p: "La voz lee todas las preguntas, las preguntas de seguimiento y los comentarios de la entrevista. Puede cambiar el volumen en cualquier momento con el control de volumen junto a Ayuda. Vea [No hay sonido o el volumen es muy bajo](#no-sound)." },
    ],
  },

  // ── Micrófono y sonido ───────────────────────────────────────────────────
  {
    id: "check-sound", category: "sound", screens: ["upload", "warmup", "interview", "live"],
    title: "Revisar mi micrófono y el sonido",
    body: [
      { steps: [
        "Si puede, use audífonos.",
        "En la pantalla de preparación, donde elige la voz femenina o masculina del entrevistador, seleccione Escuchar esta voz. Si no oye nada, vea [No hay sonido o el volumen es muy bajo](#no-sound).",
        "Use la [pregunta de calentamiento](#warm-up) antes de la entrevista para grabar algunas palabras y escucharlas.",
        "Si la grabación queda en silencio, revise que esté seleccionado el micrófono correcto y que esté [permitido en su navegador](#allow-microphone).",
      ] },
      { note: "Un lugar silencioso hace una gran diferencia, sobre todo en el [Modo Avanzado](#live-how), donde el silencio termina su respuesta." },
    ],
  },
  {
    id: "allow-microphone", category: "sound", screens: ["warmup", "interview", "live"],
    title: "Permitir el micrófono en su navegador",
    body: [
      { p: "La primera vez que grabe, su navegador le pedirá permiso para usar el micrófono. Seleccione Permitir." },
      { p: "Si lo bloqueó por error:" },
      { steps: [
        "Seleccione el ícono de candado o de configuración junto a la dirección web.",
        "Busque Micrófono y cámbielo a Permitir.",
        "Vuelva a cargar la página.",
      ] },
      { note: "En un teléfono o una tableta, revise también en la configuración del dispositivo que su navegador tenga permiso para usar el micrófono. Vea también [Navegadores y dispositivos compatibles](#browsers)." },
    ],
  },
  {
    id: "no-sound", category: "sound", screens: ["upload", "interview", "live"],
    title: "No hay sonido o el volumen es muy bajo",
    body: [
      { list: [
        "Revise el control de volumen junto a Ayuda. Si muestra un altavoz tachado, el sonido está silenciado: selecciónelo para activarlo.",
        "Revise el volumen de su dispositivo y que sus audífonos estén conectados.",
        "Si una pregunta no se reproduce en el Modo Guiado, seleccione Escuchar pregunta. Si el audio no está disponible, revise la configuración de sonido de su computadora o dispositivo. Vea [Volver a escuchar una pregunta](#replay-question).",
      ] },
    ],
  },
  {
    id: "browsers", category: "sound", screens: ["upload", "warmup", "live"],
    title: "Navegadores y dispositivos compatibles",
    body: [
      { p: "Use una versión actualizada de Chrome, Edge, Safari o Firefox. Una computadora ofrece la mejor experiencia, pero las tabletas y los teléfonos también funcionan." },
      { list: [
        "Su navegador debe tener [permiso para usar el micrófono](#allow-microphone).",
        "El Modo Avanzado necesita un navegador que pueda grabar audio. Si el suyo no puede, se le ofrecerá el Modo Guiado.",
        "Cierre otras aplicaciones que puedan estar usando el micrófono, como las videollamadas.",
      ] },
    ],
  },

  // ── Durante la entrevista ────────────────────────────────────────────────
  {
    id: "warm-up", category: "during", screens: ["warmup"],
    title: "La pregunta de calentamiento",
    body: [
      { p: "Antes de que empiece la entrevista, responde una pregunta de calentamiento sencilla. Le ayuda a [probar su micrófono](#check-sound) y a sentirse cómodo al hablar." },
      { p: "Grabe su respuesta y escúchela para revisar el sonido. Nada del calentamiento se envía para análisis ni se incluye en su informe." },
      { p: "Cuando esté listo, seleccione Continuar a la entrevista." },
    ],
  },
  {
    id: "guided-recording", category: "during", screens: ["interview"],
    title: "Grabar y terminar una respuesta (Modo Guiado)",
    body: [
      { steps: [
        "Escuche la pregunta. Puede [volver a escucharla](#replay-question).",
        "Seleccione el micrófono para empezar a grabar y dé su respuesta.",
        "Selecciónelo otra vez para detener la grabación. Puede escuchar lo que grabó.",
        "Seleccione Volver a grabar para intentarlo de nuevo, o Enviar respuesta para enviarla.",
      ] },
      { p: "Después de la última pregunta, seleccione Ver mi informe." },
      { note: "Responda como lo haría en una entrevista real: un ejemplo claro, lo que usted hizo y cuál fue el resultado." },
    ],
  },
  {
    id: "replay-question", category: "during", screens: ["interview"],
    title: "Volver a escuchar una pregunta",
    body: [
      { p: "En el Modo Guiado, seleccione Escuchar pregunta para oírla de nuevo, o Escuchar más despacio para oírla más lentamente." },
      { p: "Si el texto de la pregunta está oculto, seleccione Mostrar la pregunta para leerla." },
      { p: "En el Modo Avanzado no hay botón para repetir: [pídale al entrevistador que la repita](#live-repeat), por ejemplo \"Could you repeat the question, please?\"" },
    ],
  },
  {
    id: "follow-ups", category: "during", screens: ["interview", "live", "report"],
    title: "Preguntas de seguimiento",
    body: [
      { p: "Como un entrevistador real, el simulador puede hacer una breve pregunta de seguimiento cuando falta algo importante en su respuesta, por ejemplo un ejemplo específico o cuál fue el resultado." },
      { list: [
        "Su respuesta a la pregunta de seguimiento se evalúa junto con su respuesta principal.",
        "Una pregunta de seguimiento no es una penalización, pero responder de forma completa la primera vez es un muy buen hábito en las entrevistas. Su informe las cuenta como [Completas en la primera respuesta](#complete-first).",
      ] },
    ],
  },
  {
    id: "restart-or-end", category: "during", screens: ["warmup", "interview"],
    title: "Reiniciar o Finalizar: qué hace cada uno",
    body: [
      { list: [
        "Reiniciar empieza la misma entrevista otra vez desde la Pregunta 1 y borra sus respuestas hasta ese momento. Se conservan su currículum, la descripción del puesto y la configuración. El primer reinicio de una entrevista es gratuito; cada reinicio posterior usa una [sesión](#what-uses-session).",
        "Finalizar entrevista detiene la entrevista y borra sus respuestas y su avance. La sesión usada no se restablece.",
      ] },
      { note: "Si solo necesita una pausa, no tiene que finalizar la entrevista. Su entrevista sigue activa durante 60 minutos, siempre que deje la aplicación abierta en su navegador." },
    ],
  },
  {
    id: "prep-failed", category: "during", screens: ["prepare"],
    title: "Si falla la preparación",
    body: [
      { p: "Crear sus preguntas suele tardar menos de un minuto. Si ve \"No pudimos preparar su entrevista\", seleccione Intentar de nuevo." },
      { p: "Si sigue pasando, la causa puede estar en nuestros servidores. Espere unos minutos y vuelva a intentarlo, o comuníquese con soporte para que podamos revisarlo." },
    ],
  },

  // ── Modo Avanzado ────────────────────────────────────────────────────────
  {
    id: "live-how", category: "live", screens: ["live", "upload"],
    title: "Cómo funciona el Modo Avanzado",
    body: [
      { p: "El Modo Avanzado se parece más a una entrevista real: solo audio, sin el texto de las preguntas y sin botones que presionar entre preguntas." },
      { steps: [
        "El entrevistador hace la pregunta.",
        "Su micrófono se activa y una [cuenta regresiva](#live-countdown) muestra cuánto tiempo tiene para empezar a responder.",
        "Dé su respuesta. Una vez que empieza, no hay límite de tiempo.",
        "Cuando deja de hablar, el entrevistador continúa. Vea [Terminar una respuesta](#live-ending).",
      ] },
      { note: "Use audífonos en un lugar silencioso. La ayuda está disponible entre preguntas, no mientras se reproduce una pregunta ni mientras usted responde. ¿No sabe qué modo elegir? Vea [Modo Guiado o Modo Avanzado](#guided-or-live)." },
    ],
  },
  {
    id: "live-countdown", category: "live", screens: ["live"],
    title: "La cuenta regresiva de 30 segundos",
    body: [
      { p: "Después de cada pregunta, tiene 30 segundos para empezar a responder (20 segundos para una [pregunta de seguimiento](#follow-ups)). La cuenta regresiva se detiene en cuanto empieza a hablar." },
      { p: "Si no empieza a tiempo, el entrevistador dice \"Let's move on\" y pasa a la siguiente pregunta. Su informe muestra \"Sin respuesta\" para esa pregunta, y cuenta como no respondida." },
      { note: "Tómese un momento para pensar y luego empiece a hablar. Una vez que empiece, siga: unos 3 segundos de silencio [terminan su respuesta](#live-ending)." },
    ],
  },
  {
    id: "live-ending", category: "live", screens: ["live"],
    title: "Terminar una respuesta y \"Terminé\"",
    body: [
      { p: "Su respuesta termina cuando deja de hablar durante unos 3 segundos, o cuando selecciona Terminé." },
      { list: [
        "Las pausas cortas para pensar no son un problema: los primeros segundos de una respuesta nunca la terminan.",
        "Seleccione Terminé si el ruido de fondo mantiene el micrófono escuchando después de que usted terminó.",
        "Todo lo que dijo se evalúa.",
      ] },
    ],
  },
  {
    id: "live-repeat", category: "live", screens: ["live"],
    title: "Pedirle al entrevistador que repita",
    body: [
      { p: "Solo pídalo, como lo haría en una entrevista real (en inglés):" },
      { list: [
        "\"Could you repeat the question, please?\"",
        "\"Sorry, I didn't understand the question.\"",
      ] },
      { p: "El entrevistador repite la pregunta con las mismas palabras, y la [cuenta regresiva](#live-countdown) vuelve a empezar. Pedir que repita no cuenta como respuesta." },
    ],
  },

  // ── Su informe y sus puntuaciones ────────────────────────────────────────
  {
    id: "report-overview", category: "report", screens: ["report", "trial-report"],
    title: "Entender su informe",
    body: [
      { p: "Su informe tiene dos puntuaciones principales, de 0 a 100:" },
      { list: [
        "[Desempeño en la Entrevista](#interview-performance): qué tan bien funcionan sus respuestas como respuestas de entrevista.",
        "[Comunicación en Inglés Profesional](#english-dimensions): qué tan bien se comunica en inglés.",
      ] },
      { p: "Debajo de ellas, cada pregunta muestra sus puntuaciones, lo que usted dijo, cualquier [pregunta de seguimiento](#follow-ups), una [versión mejorada de su respuesta](#polished-answer), lo que hizo bien y lo que puede mejorar." },
      { p: "Las barras son verdes desde 75, ámbar de 50 a 74 y rojas por debajo de 50." },
      { note: "Las puntuaciones y los niveles de inglés son estimaciones de práctica para orientar su preparación. No son una certificación." },
    ],
  },
  {
    id: "interview-performance", category: "report", screens: ["report", "trial-report"],
    title: "Desempeño en la Entrevista: las seis dimensiones",
    body: [
      { list: [
        "Relevancia a la pregunta: ¿respondió lo que se preguntó?",
        "Evidencia y ejemplos: ¿dio un ejemplo real y específico?",
        "Organización y estructura: ¿la respuesta fue fácil de seguir?",
        "Competencia profesional: ¿mostró las habilidades que el puesto necesita?",
        "Evidencia de resultados: ¿dijo cuál fue el resultado? Aparece como \"—\" cuando la pregunta no pedía un resultado.",
        "Presencia profesional: ¿se le escuchó seguro y asumió responsabilidad?",
      ] },
      { p: "Su puntuación de Desempeño en la Entrevista es el promedio de estas dimensiones. Vea también [Entender su informe](#report-overview)." },
    ],
  },
  {
    id: "english-dimensions", category: "report", screens: ["report", "trial-report"],
    title: "Inglés profesional: las cinco dimensiones y los niveles del MCER",
    body: [
      { p: "Su inglés se mide en cinco dimensiones: gramática, vocabulario, claridad, tono profesional y fluidez." },
      { p: "Cada una se evalúa con pautas similares a las del MCER (CEFR en inglés), la escala internacional de niveles de idioma, y se convierte en una puntuación: alrededor de 45 es similar a B1, 65 es similar a B2 y 85 es similar a C1." },
      { list: [
        "Sólido: 80 o más.",
        "Funcional: de 60 a 79.",
        "En desarrollo: menos de 60.",
      ] },
      { p: "Si una respuesta es demasiado breve para evaluar su inglés de forma justa, aparece \"Respuesta demasiado breve para evaluar su inglés\" y queda fuera de su puntuación de inglés. Vea también [Entender su informe](#report-overview)." },
    ],
  },
  {
    id: "complete-first", category: "report", screens: ["report"],
    title: "\"Completas en la primera respuesta\"",
    body: [
      { p: "Este número cuenta las preguntas que respondió de forma completa la primera vez, sin necesitar una [pregunta de seguimiento](#follow-ups)." },
      { p: "En una entrevista real, las respuestas que ya incluyen un ejemplo específico y su resultado causan una muy buena impresión. Use este número para ver con qué frecuencia su primera respuesta estuvo completa." },
    ],
  },
  {
    id: "polished-answer", category: "report", screens: ["report", "trial-report"],
    title: "Usar la respuesta mejorada y sus sugerencias",
    body: [
      { p: "La respuesta mejorada es su propia respuesta reescrita en un inglés natural y profesional. Conserva lo que usted quiso decir y no agrega nada que no haya dicho." },
      { p: "Las sugerencias en negrita y en color, entre corchetes, muestran lo que agregaría una respuesta más sólida, por ejemplo un resultado o un ejemplo específico." },
      { note: "Reemplace cada sugerencia con detalles reales de su experiencia y luego practique decir la respuesta completa en voz alta. Puede intentarlo de nuevo con [Volver a practicar](#re-practice)." },
    ],
  },
  {
    id: "re-practice", category: "report", screens: ["report"],
    title: "Volver a practicar",
    body: [
      { p: "Volver a practicar le permite responder las mismas preguntas otra vez, una sola vez, para aplicar lo que aprendió de su informe." },
      { list: [
        "Usa una [sesión](#what-uses-session).",
        "Se ofrece en la página de su informe justo después de terminar la entrevista, y deja de estar disponible cuando sale de esa página o cierra sesión.",
        "Su informe compara sus nuevas puntuaciones con su primer intento.",
      ] },
      { p: "Para practicar con preguntas nuevas, prepare una nueva entrevista con el mismo currículum y la misma descripción del puesto." },
    ],
  },
  {
    id: "download-report", category: "report", screens: ["report", "downloads"],
    title: "Descargar su informe",
    body: [
      { p: "Seleccione Descargar Informe en la página de su informe para guardarlo en PDF. También puede descargarlo más tarde desde Mis Informes y Descargas, en el menú de su cuenta." },
      { p: "El PDF incluye sus puntuaciones, una página de retroalimentación sobre su inglés y todas las preguntas, con su respuesta, la respuesta mejorada y lo que puede mejorar." },
      { note: "Los informes se pueden descargar hasta que termine su acceso de 30 días. Vea [Cuánto tiempo se conservan sus informes](#report-retention)." },
    ],
  },

  // ── Cuenta, sesiones y privacidad ────────────────────────────────────────
  {
    id: "access-sessions", category: "account", screens: ["account", "upload"],
    title: "Su acceso de 30 días y 10 sesiones",
    body: [
      { p: "El acceso completo dura 30 días e incluye en total hasta 10 sesiones de entrevista, que puede usar cualquier día durante el período de acceso de 30 días." },
      { list: [
        "Sesiones usadas: aparece en la barra superior, por ejemplo \"Sesiones usadas: 3 de 10\". Pase el puntero sobre ella para ver cuándo termina su acceso.",
        "Las sesiones que no use terminan con su acceso de 30 días.",
        "Renovar inicia un nuevo período de 30 días con 10 sesiones nuevas. El acceso no se renueva automáticamente: para renovar, compre otros 30 días de acceso.",
      ] },
      { p: "Vea [Qué usa una sesión](#what-uses-session)." },
    ],
  },
  {
    id: "what-uses-session", category: "account", screens: ["account", "interview"],
    title: "Qué usa una sesión",
    body: [
      { list: [
        "Empezar una nueva entrevista, en Modo Guiado o Modo Avanzado: 1 sesión.",
        "[Volver a practicar](#re-practice): 1 sesión.",
        "Reiniciar una entrevista: el primer reinicio es gratuito; cada reinicio posterior usa 1 sesión. Vea [Reiniciar o Finalizar](#restart-or-end).",
        "Si una entrevista no logra empezar, su sesión se restablece.",
        "Si falla la [preparación de su entrevista personalizada](#prep-failed), comuníquese con soporte. Restableceremos su conteo de sesiones y revisaremos la causa.",
      ] },
      { p: "Leer su informe y descargarlo nunca usan una sesión." },
    ],
  },
  {
    id: "report-retention", category: "account", screens: ["account", "downloads", "report"],
    title: "Cuánto tiempo se conservan sus informes",
    body: [
      { p: "Su informe, y el currículum, la descripción del puesto y las respuestas con que se creó, se conservan hasta el final de su período de acceso de 30 días. Después, todo se elimina automáticamente." },
      { p: "Le enviamos un correo unas 48 horas antes de que se eliminen sus informes, para que pueda [descargar el PDF](#download-report). Una vez que termina su acceso de 30 días, ya no puede descargar sus informes anteriores." },
    ],
  },
  {
    id: "data-privacy", category: "account", screens: ["account", "upload"],
    title: "Sus datos y su privacidad",
    body: [
      { list: [
        "El currículum que sube pasa por una [revisión de privacidad](#privacy-scan) antes de usarse, y usted revisa el resultado. La aplicación nunca usa su currículum original para crear la entrevista: solo se usa la versión que usted aprueba después de la revisión, y se conserva hasta que termina su acceso de 30 días.",
        "El texto revisado de su currículum, la descripción del puesto y sus respuestas se usan solo para crear y evaluar su entrevista de práctica.",
        "Se eliminan al final de su período de acceso de 30 días (después de 24 horas en el caso de una [Prueba Gratuita](#free-deleted)).",
        "Servicios de IA procesan su entrevista para crear las preguntas, transcribir sus respuestas y darle retroalimentación.",
      ] },
      { p: "Para todos los detalles, consulte la Política de Privacidad." },
    ],
  },

  // ── Prueba Gratuita ──────────────────────────────────────────────────────
  {
    id: "free-trial", category: "free", screens: ["upload", "trial-report"],
    title: "Qué incluye la Prueba Gratuita",
    body: [
      { list: [
        "Una entrevista de práctica gratuita de 6 preguntas, en Modo Guiado.",
        "Sus resultados completos en pantalla, con retroalimentación para cada pregunta.",
        "La misma [revisión de privacidad](#privacy-scan) del currículum que en el acceso completo.",
      ] },
      { p: "Descargar el informe, [Volver a practicar](#re-practice), reiniciar y el [Modo Avanzado](#live-how) vienen con el [acceso completo de 30 días](#access-sessions). Puede verlos en la aplicación, en gris." },
    ],
  },
  {
    id: "free-deleted", category: "free", screens: ["trial-report"],
    title: "Por qué se eliminan sus resultados gratuitos",
    body: [
      { p: "Los resultados de la Prueba Gratuita no se guardan. Se eliminan cuando sale de la página de resultados o cierra sesión, y automáticamente 24 horas después de la entrevista." },
      { p: "Lea su retroalimentación antes de salir de la página. Con el acceso completo, los informes se conservan hasta que termina su acceso de 30 días y se pueden [descargar en PDF](#download-report)." },
    ],
  },
];
